import { useState, useCallback } from 'react'
import { EyeOff, Link2, Search, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useCreators } from '@/hooks/use-creators'
import { useUpdateMentionStatus, useLinkMentionToCreator } from '@/hooks/use-social-listening'
import { PostCard, type PostCardData } from '@/components/shared/post-card'
import type { BrandMention } from '@/lib/api/modules/social-listening'

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<BrandMention['status'], string> = {
  new: 'Nova',
  reviewed: 'Revisada',
  linked: 'Vinculada',
  ignored: 'Ignorada',
}

const STATUS_CLASSES: Record<BrandMention['status'], string> = {
  new: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/20',
  reviewed: 'bg-blue-500/15 text-blue-600 border-blue-500/20',
  linked: 'bg-violet-500/15 text-violet-600 border-violet-500/20',
  ignored: 'bg-muted text-muted-foreground border-border',
}

const AVATAR_GRADIENTS = [
  'from-pink-500 to-rose-500',
  'from-orange-400 to-pink-500',
  'from-violet-500 to-purple-600',
  'from-sky-400 to-blue-600',
  'from-emerald-400 to-teal-600',
  'from-amber-400 to-orange-500',
]

function avatarGradient(username: string): string {
  let hash = 0
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]
}

// ─── Mapper ──────────────────────────────────────────────────────────────────

function toPostCardData(mention: BrandMention): PostCardData {
  return {
    id: mention.id,
    authorUsername: mention.authorUsername,
    authorId: mention.authorId,
    authorProfileUrl: mention.authorProfileUrl,
    platform: mention.platform,
    mediaType: mention.mediaType,
    detectedBySource: mention.detectedBySource,
    caption: mention.caption,
    postUrl: mention.postUrl,
    mediaUrl: mention.mediaUrl,
    thumbnailUrl: mention.thumbnailUrl,
    r2MediaKey: mention.r2MediaKey,
    r2ThumbnailKey: mention.r2ThumbnailKey,
    carouselMedia: mention.carouselMedia,
    mediaWidth: mention.mediaWidth,
    mediaHeight: mention.mediaHeight,
    likes: mention.likes,
    comments: mention.comments,
    views: mention.views,
    postedAt: mention.postedAt,
    createdAt: mention.createdAt,
  }
}

// ─── Creator search popover ─────────────────────────────────────────────────

function CreatorSearchPopover({
  onSelect,
  isPending,
}: {
  onSelect: (creatorId: string) => void
  isPending: boolean
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { data, isLoading } = useCreators(1, 10, { search: search || undefined })
  const creators = data?.data || []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ChonkyButton variant="secondary" size="sm" className="gap-1.5 text-xs" loading={isPending}>
          <Link2 className="h-3 w-3" />
          Vincular
        </ChonkyButton>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <input
            type="text"
            placeholder="Buscar creator..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <div className="max-h-48 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && creators.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">
              Nenhum creator encontrado
            </p>
          )}
          {!isLoading &&
            creators.map((creator) => (
              <button
                key={creator.id}
                type="button"
                onClick={() => {
                  onSelect(creator.id)
                  setOpen(false)
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
              >
                <div
                  className={cn(
                    'h-6 w-6 rounded-full bg-linear-to-br flex items-center justify-center shrink-0',
                    'text-white text-[10px] font-bold',
                    avatarGradient(creator.name)
                  )}
                >
                  {creator.name[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{creator.name}</p>
                  {creator.instagramUsername && (
                    <p className="text-[11px] text-muted-foreground truncate">
                      @{creator.instagramUsername}
                    </p>
                  )}
                </div>
              </button>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─── Mention actions bar ────────────────────────────────────────────────────

function MentionActions({ mention }: { mention: BrandMention }) {
  const updateStatus = useUpdateMentionStatus()
  const linkCreator = useLinkMentionToCreator()

  const handleLink = useCallback(
    (creatorId: string) => {
      linkCreator.mutate({ id: mention.id, creatorId })
    },
    [mention.id, linkCreator]
  )

  if (mention.status === 'linked' || mention.status === 'ignored') return null

  return (
    <div className="shrink-0 border-t border-border px-4 py-2.5">
      <div className="flex items-center gap-2">
        <ChonkyButton
          variant="secondary"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={() => updateStatus.mutate({ id: mention.id, status: 'ignored' })}
          loading={updateStatus.isPending}
        >
          <EyeOff className="h-3 w-3" />
          Ignorar
        </ChonkyButton>
        <CreatorSearchPopover onSelect={handleLink} isPending={linkCreator.isPending} />
      </div>
    </div>
  )
}

// ─── Exported wrapper ────────────────────────────────────────────────────────

export function MentionCard({ mention }: { mention: BrandMention }) {
  const [now] = useState(Date.now)
  const isRecent =
    !!mention.createdAt && now - new Date(mention.createdAt).getTime() < 48 * 60 * 60 * 1000

  return (
    <PostCard
      post={toPostCardData(mention)}
      statusBadge={
        <Badge className={cn('border text-[10px] px-1.5 py-0', STATUS_CLASSES[mention.status])}>
          {STATUS_LABELS[mention.status]}
        </Badge>
      }
      lightboxActions={<MentionActions mention={mention} />}
      isRecent={isRecent}
    />
  )
}
